import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// Automated email series (admin-api routes/email-series.ts, ADR-0011): a
// trigger such as "trial ended" enrols customers, then each step sends N days
// later. Copy, timing and the offer are edited here; who a trigger enrols is
// decided in code on the server.

const BASE = '/email-series';

export type SeriesStatus = 'paused' | 'dry_run' | 'live';

export interface SeriesStats {
  enrolled: number;
  active: number;
  codesIssued: number;
  converted: number;
  unsubscribed: number;
  redeemed: number;
  sent: number;
  dryRun: number;
}

export interface EmailSeries {
  id: number;
  name: string;
  triggerType: string;
  status: SeriesStatus;
  discountId: number | null;
  discountCode: string | null;
  discountLabel: string | null;
  codeValidDays: number | null;
  sendHour: number;
  lookbackDays: number;
  reenrolAfterDays: number;
  updatedAt: string;
  updatedBy: string | null;
  stats: SeriesStats;
}

export interface SeriesTrigger {
  type: string;
  description: string;
}

export interface SeriesVariable {
  name: string;
  description: string;
}

export interface SeriesList {
  series: EmailSeries[];
  triggers: SeriesTrigger[];
  variables: SeriesVariable[];
}

export interface SeriesStep {
  id: number;
  stepOrder: number;
  name: string;
  sendAfterDays: number;
  subject: string;
  preheader: string | null;
  htmlBody: string;
  textBody: string | null;
  isActive: boolean;
  updatedAt: string;
  updatedBy: string | null;
  stats: { sent: number; dryRun: number; skipped: number; failed: number };
}

/** A discount the series may hand out: this brand's active percentage or fixed-amount discounts. */
export interface SeriesDiscountOption {
  id: number;
  discountCode: string;
  name: string;
  /** e.g. "50% off every payment for 1 month". */
  label: string | null;
  synced: boolean;
  /** 'YYYY-MM-DD', or null when it never ends. */
  endDate: string | null;
}

export interface SeriesDetail {
  series: EmailSeries;
  steps: SeriesStep[];
  /** Block dry run and live. */
  problems: string[];
  /** Worth a second look; don't block. */
  warnings: string[];
  discountOptions: SeriesDiscountOption[];
}

export interface SeriesUpdate {
  name?: string;
  status?: SeriesStatus;
  discountId?: number | null;
  codeValidDays?: number | null;
  sendHour?: number;
  lookbackDays?: number;
  reenrolAfterDays?: number;
}

export interface StepDraft {
  name: string;
  sendAfterDays: number;
  subject: string;
  preheader: string;
  htmlBody: string;
  textBody: string;
  isActive: boolean;
}

export type EmailContent = Pick<StepDraft, 'subject' | 'preheader' | 'htmlBody' | 'textBody'>;

export interface SeriesPreview {
  subject: string;
  html: string;
  text: string;
  unknownVariables: string[];
}

export interface SeriesCandidates {
  count: number;
  sample: { userId: number; /** 'YYYY-MM-DD' */ triggerDate: string; name: string | null; email: string | null }[];
}

export type EnrolmentStatus = 'active' | 'completed' | 'converted' | 'unsubscribed' | 'stopped';

export interface EnrolmentSend {
  stepOrder: number;
  stepName: string;
  status: 'sending' | 'sent' | 'dry_run' | 'skipped' | 'failed';
  sentAt: string | null;
  skippedReason: string | null;
  error: string | null;
  subject: string | null;
}

export interface Enrolment {
  id: number;
  userId: number;
  name: string | null;
  email: string | null;
  /** 'YYYY-MM-DD' on the Brisbane calendar. */
  triggerDate: string;
  status: EnrolmentStatus | string;
  stoppedReason: string | null;
  isDryRun: boolean;
  promoCode: string | null;
  promoExpiresAt: string | null;
  convertedAt: string | null;
  createdAt: string;
  sends: EnrolmentSend[];
}

export interface EnrolmentPage {
  enrolments: Enrolment[];
  total: number;
}

function useInvalidateSeries() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['email-series'] });
}

export function useEmailSeriesList() {
  return useQuery({
    queryKey: ['email-series', 'list'],
    queryFn: () => apiClient<SeriesList>(BASE),
  });
}

export function useEmailSeries(id: number) {
  return useQuery({
    queryKey: ['email-series', 'detail', id],
    queryFn: () => apiClient<SeriesDetail>(`${BASE}/${id}`),
  });
}

export function useCreateEmailSeries() {
  const invalidate = useInvalidateSeries();
  return useMutation({
    mutationFn: async (draft: { name: string; triggerType: string }) =>
      (await apiClient<{ series: EmailSeries }>(BASE, { method: 'POST', body: JSON.stringify(draft) })).series,
    onSuccess: invalidate,
  });
}

export function useUpdateEmailSeries() {
  const invalidate = useInvalidateSeries();
  return useMutation({
    mutationFn: ({ id, ...fields }: SeriesUpdate & { id: number }) =>
      apiClient<{ series: EmailSeries; problems: string[]; warnings: string[] }>(`${BASE}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(fields),
      }),
    onSuccess: invalidate,
  });
}

export function useCreateSeriesStep() {
  const invalidate = useInvalidateSeries();
  return useMutation({
    mutationFn: ({ seriesId, ...draft }: StepDraft & { seriesId: number }) =>
      apiClient<{ step: SeriesStep }>(`${BASE}/${seriesId}/steps`, { method: 'POST', body: JSON.stringify(draft) }),
    onSuccess: invalidate,
  });
}

export function useUpdateSeriesStep() {
  const invalidate = useInvalidateSeries();
  return useMutation({
    mutationFn: ({ seriesId, stepId, ...fields }: Partial<StepDraft> & { seriesId: number; stepId: number }) =>
      apiClient<{ step: SeriesStep }>(`${BASE}/${seriesId}/steps/${stepId}`, { method: 'PUT', body: JSON.stringify(fields) }),
    onSuccess: invalidate,
  });
}

export function useDeleteSeriesStep() {
  const invalidate = useInvalidateSeries();
  return useMutation({
    mutationFn: ({ seriesId, stepId }: { seriesId: number; stepId: number }) =>
      apiClient<{ success: boolean }>(`${BASE}/${seriesId}/steps/${stepId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** The step as a customer would see it, with a sample name and code. Only fetched while shown. */
export function useSeriesPreview(seriesId: number, content: EmailContent, enabled: boolean) {
  return useQuery({
    queryKey: ['email-series', 'preview', seriesId, content],
    queryFn: () => apiClient<SeriesPreview>(`${BASE}/${seriesId}/preview`, { method: 'POST', body: JSON.stringify(content) }),
    enabled: enabled && content.htmlBody.trim().length > 0,
    placeholderData: keepPreviousData,
  });
}

export function useSendSeriesTest() {
  return useMutation({
    mutationFn: ({ seriesId, ...content }: EmailContent & { seriesId: number; to?: string }) =>
      apiClient<{ sentTo: string }>(`${BASE}/${seriesId}/test`, { method: 'POST', body: JSON.stringify(content) }),
  });
}

/** Who the next live pass would enrol. Fetched fresh each time the go-live check opens. */
export function useSeriesCandidates(seriesId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['email-series', 'candidates', seriesId],
    queryFn: () => apiClient<SeriesCandidates>(`${BASE}/${seriesId}/candidates`),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSeriesEnrolments(seriesId: number, params: { status?: string; limit: number; offset: number }) {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
    ...(params.status ? { status: params.status } : {}),
  });
  return useQuery({
    queryKey: ['email-series', 'enrolments', seriesId, params],
    queryFn: () => apiClient<EnrolmentPage>(`${BASE}/${seriesId}/enrolments?${query.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export interface StopEnrolmentResult {
  success: boolean;
  /** Whether their personal Stripe code was switched off. */
  codeDeactivated: boolean;
  hadCode: boolean;
}

export function useStopEnrolment() {
  const invalidate = useInvalidateSeries();
  return useMutation({
    mutationFn: (enrolmentId: number) =>
      apiClient<StopEnrolmentResult>(`${BASE}/enrolments/${enrolmentId}/stop`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
